import React, { useState, useEffect } from "react";
import { Card, Dialog, Text, Link, Box, Button } from "common";
import CustomFileUpload from "components/BundlePromotion/CartBundleForm/CustomFileUpload";
import { setLocation } from "store/actions/auth.actions";
import { useDispatch } from "react-redux";
import ApprovalBundleScreen from "components/BundlePromotion/ApprovalBundleScreen";
import { useStyles } from "../../components/BundlePromotion/CartBundleForm/CartBundleForm.style";
import {
  getBulkBundleResource,
  createBulkBundles,
} from "store/actions/bulkBundleUtility.actions";
import { useApiInfo } from "hooks";
import { CREATE_BULK_BUNDLE_JOB } from "store/store.types";
import { clearApi } from "store/actions/api.actions";

const BulkBundleUtility = () => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const [openApproval, setOpenApproval] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const [bulkBtnText, setBulkBtnText] = useState<string>("");

  const { error, isLoading, done } = useApiInfo(CREATE_BULK_BUNDLE_JOB);

  const onCloseNotification = () => {
    dispatch(clearApi(CREATE_BULK_BUNDLE_JOB));
  };

  const updateApprovalScreen = (status: boolean) => () => {
    setOpenApproval(status);
    onCloseNotification();
  };

  const onSubmit = () => {
    if (bulkBtnText === "Cancel") {
      dispatch(setLocation("/cart-bundles"));
    } else if (file) {
      const filePayload = new FormData();
      filePayload.append("file", file, file?.name);
      filePayload.append("type", "BUNDLE_CAMPAIGN");
      dispatch(createBulkBundles(filePayload));
      dispatch(clearApi(CREATE_BULK_BUNDLE_JOB));
    }
  };

  const confirmationHandle = (bulkBtnText: string) => {
    if (bulkBtnText === "Submit") {
      setOpenApproval(true);
      setBulkBtnText("Submit");
    } else {
      setOpenApproval(true);
      setBulkBtnText("Cancel");
    }
  };

  useEffect(() => {
    if (!isLoading && !error && done) {
      dispatch(setLocation("/bulk-campaign-jobs"));
    }
  }, [isLoading, error, done, dispatch]);

  // This is used to convert string error into an arry of errors
  const errorList = (error ?? "")
    .replace(/^\[/, "")
    .replace(/\]$/, "")
    .split(" , ")
    .map((e) => e.trim())
    .filter(Boolean);

  return (
    <Card padding={["40px 20px", "40px 20px", "50px 100px", "50px 100px"]}>
      <Dialog
        open={openApproval}
        onClose={updateApprovalScreen(false)}
        dialogTitle={
          <Text className={classes.dialogText}>
            {bulkBtnText === "Submit"
              ? "Are you sure you want to upload this file?"
              : "Are you sure you want to discard this change?"}
          </Text>
        }
      >
        <ApprovalBundleScreen
          updateApprovalScreen={updateApprovalScreen(false)}
          onSubmit={onSubmit}
          isLoading={isLoading}
          errorList={errorList}
        />
      </Dialog>
      <Text
        fontSize="28px"
        marginBottom="8px"
        fontWeight={600}
        data-testid="header-bulk-utility"
      >
        Bulk Campaign Creation
      </Text>
      <Text
        fontSize="16px"
        marginBottom="20px"
        data-testid="title-bulk-utility"
      >
        Please upload your file to effortlessly create bulk campaigns.
      </Text>
      <Link
        data-testid="download-bulk-camp-temp"
        variant="default"
        color="primary"
        onClick={() => dispatch(getBulkBundleResource())}
      >
        Download sample template
      </Link>
      <Box mt={1}>
        <CustomFileUpload
          name="bulkUtility"
          fileSelected={!!file}
          onFileSelect={(file) => setFile(file)}
          uploadStrategy={"RETURN_FILE"}
        />
      </Box>
      <Box display="flex" mt={2}>
        {["Submit", "Cancel"].map((btnText) => {
          return (
            <Button
              key={btnText}
              variant={btnText === "Submit" ? "contained" : "outlined"}
              color={btnText === "Submit" ? "primary" : "default"}
              data-testid={`bulk-utility-${btnText}-btn`}
              style={{ marginRight: 16 }}
              onClick={() => confirmationHandle(btnText)}
              disabled={!file}
            >
              {btnText}
            </Button>
          );
        })}
      </Box>
    </Card>
  );
};

export default BulkBundleUtility;
